<template>
	<q-card class="assessment-card q-mb-sm" :id="assessmentId">
		<q-item :link="!isEditing" v-ripple="!isEditing" tabindex="0" @click.native.self="handleClick" @keyup.native.enter="handleClick">
			<q-item-side @click.native="handleClick" :avatar="value.institution.logo" v-if="value.institution.logo" />
			<q-item-main @click.native="handleClick">
				<q-item-tile label>{{ displayTitle }}</q-item-tile>
				<q-item-tile sublabel>{{ displaySubtitle }}</q-item-tile>
			</q-item-main>
			<q-item-side right>
				<q-spinner v-if="!value.isEditing && value.isPersisting" size="30" class="q-mr-sm" color="secondary"  />
				<q-icon @click.native="handleClick" v-if="!isEditing" size="20pt" name="keyboard_arrow_right" />
			</q-item-side>
		</q-item>

		<q-slide-transition>
			<div>
				<q-card-main v-if="isEditing" @keyup.native.esc="cancel">
					<section>
						<div class="row gutter-sm">
							<div class="col-xs-12">
								<InstitutionSelect v-model="value" />
							</div>
						</div>
					</section>

					<section class="q-mt-md">
						<div class="row gutter-sm">
							<div class="col-xs-12 col-md-4">
								<YearInput v-model="value" />
							</div>
							<div class="col-xs-12 col-md-6">
								<AssessmentSelect v-model="value" />
							</div>
							<div class="col-xs-12 col-md-2">
								<ScoreInput v-model="value" @change="save" />
							</div>
						</div>
					</section>
				</q-card-main>
				<q-card-separator v-if="isEditing" />
				<q-card-actions v-if="isEditing">
					<q-btn v-if="isEditing" label="Save" flat color="secondary" @click="save" />
					<q-btn v-if="isEditing && isNew" label="Cancel" flat color="faded" @click="cancel" />
					<q-spinner v-if="value.isPersisting" size="30" class="q-mr-sm" color="secondary"  />
				</q-card-actions>
			</div>
		</q-slide-transition>
	</q-card>

</template>

<script lang="ts">
import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator';
import { mapActions } from 'vuex';
import { AssessmentEntry } from '@/models';
import YearInput from '../YearInput.vue';
import ScoreInput from './ScoreInput.vue';
import AssessmentSelect from './AssessmentSelect.vue';
import InstitutionSelect from './InstitutionSelect.vue';

@Component({
	methods: mapActions('wmct', [
		'persistLearning',
		'cancelEditLearning',
	]),
	components: {
		YearInput,
		ScoreInput,
		AssessmentSelect,
		InstitutionSelect,
	},
})
export default class Assessment extends Vue {
	@Prop() public value!: AssessmentEntry;

	private persistLearning!: ({ learning }: { learning: AssessmentEntry }) => Promise<void>;
	private cancelEditLearning!: ({ learning }: { learning: AssessmentEntry }) => Promise<void>;

	private get isEditing() {
		return this.value.isEditing;
	}

	@Watch('isEditing')
	public onEditing(editing: boolean) {
		if (editing) {
			const assessment: AssessmentEntry = this.value;

			(this as any).$scrollToId(`assessment-${assessment.id}`);
		}
	}

	private handleClick() {
		if (!this.isEditing) {
			this.$emit('click', this.value);
		}
	}

	public get assessmentId() {
		return 'assessment-' + this.value.id;
	}

	private get displaySubtitle() {
		if (this.value.institution.value == null) {
			return '';
		} else if (this.value.academicYear) {
			return `${this.value.institution.label} (${this.value.academicYear})`;
		} else {
			return this.value.institution.label;
		}
	}

	private get displayTitle() {
		if (this.value.course.value == null) {
			return 'New Assessment';
		} else {
			return this.value.course.label;
		}
	}

	private async save() {
		const assessment = this.value;

		await this.persistLearning({ learning: assessment });
	}

	private async cancel() {
		const assessment = this.value;

		await this.cancelEditLearning({ learning: assessment });
	}

	private get isNew() {
		const course = this.value;

		return !course.id;
	}
}
</script>